import { useState } from 'react';

import type { ChannelDto, Role, TeamMemberDto } from '../../api/types';
import { useChannelList } from '../../hooks/use-channels';
import { useSession } from '../../hooks/use-session';
import {
  useCreateMember,
  usePatchMember,
  useResetMemberPassword,
  useSetMemberDisabled,
  useTeamMembers,
} from '../../hooks/use-team';
import { OneTimePasswordPanel } from '../account/OneTimePasswordPanel';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';

const ROLES: Role[] = ['owner', 'lead', 'manager'];
const ROLE_LABELS: Record<Role, string> = { owner: 'Владелец', lead: 'Руководитель', manager: 'Менеджер', reviewer: 'Ревьюер' };

interface Shown {
  email: string;
  password: string;
  title: string;
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function ChannelChecklist({
  channels,
  value,
  onChange,
  idPrefix,
}: {
  channels: ChannelDto[];
  value: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
}) {
  if (channels.length === 0) return <p className="text-xs text-muted-fg">Каналов пока нет — доступ можно выдать позже.</p>;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {channels.map((c) => (
        <label key={c.id} className="flex items-center gap-1 text-xs" htmlFor={`${idPrefix}-${c.id}`}>
          <input
            id={`${idPrefix}-${c.id}`}
            type="checkbox"
            checked={value.includes(c.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, c.id] : value.filter((id) => id !== c.id))}
          />
          {c.title}
        </label>
      ))}
    </div>
  );
}

/**
 * Настройки → Команда. The owner creates accounts (the server generates a
 * temporary password, shown once), resets passwords, disables/enables members
 * and changes roles. A lead sees the list and may only change which channels
 * a manager can work in.
 */
export function TeamSection({ canManage }: { canManage: boolean }) {
  const { me } = useSession();
  const { data, isLoading } = useTeamMembers();
  const { data: channelData } = useChannelList();
  const channels = channelData ?? [];
  const createMember = useCreateMember();
  const patchMember = usePatchMember();
  const resetPassword = useResetMemberPassword();
  const setDisabled = useSetMemberDisabled();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('manager');
  const [access, setAccess] = useState<string[]>([]);
  const [shown, setShown] = useState<Shown | null>(null);
  const [editing, setEditing] = useState<{ userId: string; channelIds: string[] } | null>(null);

  const canEditAccess = canManage || me?.role === 'lead';

  function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    createMember.mutate(
      { email, name, role, channel_access: role === 'manager' ? access : [] },
      {
        onSuccess: (res) => {
          // The password goes into a panel the owner closes explicitly — never into a toast that vanishes.
          setShown({ email: res.email, password: res.temp_password, title: 'Учётная запись создана. Временный пароль:' });
          setEmail('');
          setName('');
          setAccess([]);
        },
        onError: (err) => toast.push(errorText(err, 'Не удалось создать учётную запись')),
      },
    );
  }

  function handleReset(m: TeamMemberDto) {
    if (!window.confirm(`Сбросить пароль ${m.email}? Все его сессии и подключённые расширения будут отключены.`)) return;
    resetPassword.mutate(m.userId, {
      onSuccess: (res) => setShown({ email: m.email, password: res.temp_password, title: 'Пароль сброшен. Новый временный пароль:' }),
      onError: (err) => toast.push(errorText(err, 'Не удалось сбросить пароль')),
    });
  }

  function handleDisable(m: TeamMemberDto, disabled: boolean) {
    if (disabled && !window.confirm(`Отключить ${m.email}? Он сразу потеряет доступ к кабинету и расширению.`)) return;
    setDisabled.mutate(
      { userId: m.userId, disabled },
      { onError: (err) => toast.push(errorText(err, disabled ? 'Не удалось отключить' : 'Не удалось включить')) },
    );
  }

  function changeRole(userId: string, newRole: Role) {
    patchMember.mutate({ userId, body: { role: newRole } }, { onError: (err) => toast.push(errorText(err, 'Не удалось изменить роль')) });
  }

  function saveAccess() {
    if (!editing) return;
    patchMember.mutate(
      { userId: editing.userId, body: { channel_access: editing.channelIds } },
      { onSuccess: () => setEditing(null), onError: (err) => toast.push(errorText(err, 'Не удалось изменить доступ')) },
    );
  }

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-4">
      {shown && <OneTimePasswordPanel {...shown} onClose={() => setShown(null)} />}

      {canManage && (
        <form onSubmit={submitCreate} className="space-y-3 rounded border border-border p-3" aria-label="Новый сотрудник">
          <div className="text-sm font-medium">Новый сотрудник</div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-xs text-muted-fg" htmlFor="invite-name">
                Имя
              </label>
              <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-fg" htmlFor="invite-email">
                Email
              </label>
              <Input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-fg" htmlFor="invite-role">
                Роль
              </label>
              <Select id="invite-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </Select>
            </div>
            <Button type="submit" disabled={createMember.isPending}>
              Создать учётную запись
            </Button>
          </div>
          {role === 'manager' && (
            <div>
              <div className="mb-1 text-xs text-muted-fg">Доступ к каналам (менеджер видит только отмеченные)</div>
              <ChannelChecklist channels={channels} value={access} onChange={setAccess} idPrefix="invite-ch" />
            </div>
          )}
          <p className="text-xs text-muted-fg">
            Пароль сотруднику придумывать не нужно: система сгенерирует временный и покажет его один раз.
          </p>
        </form>
      )}

      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs uppercase text-muted-fg">
          <tr>
            <th className="px-3 py-2">Имя</th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Роль</th>
            <th className="px-3 py-2">Каналы</th>
            <th className="px-3 py-2">Статус</th>
            {canManage && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {(data?.members ?? []).map((m) => {
            const isSelf = m.userId === me?.user.id;
            const accessEditable = canEditAccess && m.role === 'manager';
            const isEditing = editing?.userId === m.userId;
            return (
              <tr key={m.userId} className="border-b border-border align-top">
                <td className="px-3 py-2">{m.name}</td>
                <td className="px-3 py-2 text-xs text-muted-fg">{m.email}</td>
                <td className="px-3 py-2">
                  {canManage ? (
                    <Select aria-label={`Роль ${m.email}`} value={m.role} onChange={(e) => changeRole(m.userId, e.target.value as Role)}>
                      {(ROLES.includes(m.role) ? ROLES : [...ROLES, m.role]).map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Badge>{ROLE_LABELS[m.role]}</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {m.role !== 'manager' && <span className="text-muted-fg">все</span>}
                  {m.role === 'manager' && !isEditing && (
                    <span className="flex items-center gap-2">
                      <span className="text-muted-fg">{m.channelIds.length}</span>
                      {accessEditable && (
                        <button type="button" className="underline" onClick={() => setEditing({ userId: m.userId, channelIds: m.channelIds })}>
                          изменить
                        </button>
                      )}
                    </span>
                  )}
                  {isEditing && editing && (
                    <div className="space-y-2">
                      <ChannelChecklist
                        channels={channels}
                        value={editing.channelIds}
                        onChange={(next) => setEditing({ userId: m.userId, channelIds: next })}
                        idPrefix={`acc-${m.userId}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveAccess} disabled={patchMember.isPending}>
                          Сохранить
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                          Отмена
                        </Button>
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {m.disabledAt ? (
                    <Badge>Отключён</Badge>
                  ) : m.mustChangePassword ? (
                    <span className="text-amber-700">Временный пароль</span>
                  ) : (
                    <span className="text-muted-fg">Активен</span>
                  )}
                </td>
                {canManage && (
                  <td className="px-3 py-2">
                    {!isSelf && (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="secondary" onClick={() => handleReset(m)} disabled={resetPassword.isPending}>
                          Сбросить пароль
                        </Button>
                        {m.disabledAt ? (
                          <Button size="sm" variant="secondary" onClick={() => handleDisable(m, false)}>
                            Включить
                          </Button>
                        ) : (
                          <Button size="sm" variant="danger" onClick={() => handleDisable(m, true)}>
                            Отключить
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
